import { z } from 'zod';

import { requestJson } from './requests';
import { ApiResponseError, assertOk } from './response';
import { resolveApiFetch } from './shared';
import { getClientTimezone } from '@/lib/utils/timezone';

export type QrMetricsPeriod = 'today' | 'all' | 'custom';

export interface FetchQrMetricsOptions {
  period?: QrMetricsPeriod;
  from?: string;
  to?: string;
  timezone?: string;
  signal?: AbortSignal;
}

export interface QrMetricsPeriodDescriptor {
  period: QrMetricsPeriod;
  timezone: string;
  label: string;
  from: string | null;
  to: string | null;
  startUtc: number | null;
  endUtc: number | null;
}

export interface QrMetricsTotals {
  totalTables: number;
  scansCount: number;
  ordersCount: number;
  revenueCents: number;
  lastActivityAt: string | null;
}

export interface QrTableMetrics {
  tableId: string;
  tableNumber: number | null;
  qrCodeSlug: string | null;
  scansCount: number;
  ordersCount: number;
  revenueCents: number;
  lastActivityAt: string | null;
}

export interface QrMetricsResponse {
  generatedAt: string;
  period: QrMetricsPeriodDescriptor;
  totals: QrMetricsTotals;
  tables: QrTableMetrics[];
}

export type QrCodeStatus = 'active' | 'revoked' | 'expired';

export interface QrCodeSummary {
  id: string;
  status: QrCodeStatus;
  tableId: string;
  tableNumber: number | null;
  displayOrder: number;
  qrCodeSlug: string;
  locationId: string | null;
  zoneId: string | null;
  zoneName: string | null;
  zoneColor: string | null;
  tokenFingerprint: string;
  issuedAt: number;
  expiresAt: number;
  revokedAt: number | null;
  revokedBy: string | null;
  scanCount: number;
  lastScannedAt: number | null;
  tamperCount: number;
  lastTamperAt: number | null;
  updatedAt: number;
}

export interface TamperIncidentSummary {
  id: string;
  qrCodeId: string | null;
  tableId: string | null;
  tableNumber: number | null;
  qrCodeSlug: string | null;
  locationId: string | null;
  locationName: string | null;
  zoneId: string | null;
  zoneName: string | null;
  zoneColor: string | null;
  severity: string;
  source: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  detectedAt: number;
  resolvedAt: number | null;
}

export interface ListTamperIncidentsOptions {
  tableId?: string;
  severity?: string;
  limit?: number;
  since?: string;
  signal?: AbortSignal;
}

export type QrTimelineEntryType = 'issued' | 'revoked' | 'tamper' | 'incident' | 'regenerated' | 'refresh';

export type QrTimelineSeverity = 'info' | 'warning' | 'critical';

export interface QrTimelineEntry {
  id: string;
  tableId: string;
  qrCodeId: string | null;
  type: QrTimelineEntryType;
  occurredAt: number;
  severity: QrTimelineSeverity;
  description: string | null;
  source: string | null;
  actorId: string | null;
  resolvedAt: number | null;
  metadata: Record<string, unknown> | null;
}

export interface FetchQrTimelineOptions {
  limit?: number;
  since?: number;
  signal?: AbortSignal;
}

export interface ListQrCodesOptions {
  statuses?: QrCodeStatus[];
  tableIds?: string[];
  zoneId?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

const QrMetricsEnvelopeSchema = z
  .object({
    generatedAt: z.string().optional(),
    period: z.unknown().optional(),
    totals: z.unknown().optional(),
    tables: z.unknown().optional(),
    metrics: z
      .object({
        generatedAt: z.string().optional(),
        period: z.unknown().optional(),
        totals: z.unknown().optional(),
        tables: z.unknown().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const QrCodesResponseSchema = z
  .object({
    codes: z.array(z.record(z.unknown())).optional(),
  })
  .passthrough();

const QrReorderResponseSchema = z
  .object({
    updated: z.number().optional(),
  })
  .passthrough();

const QrTimelineResponseSchema = z
  .object({
    entries: z.array(z.record(z.unknown())).optional(),
  })
  .passthrough();

const QrTamperIncidentsResponseSchema = z
  .object({
    incidents: z.array(z.record(z.unknown())).optional(),
  })
  .passthrough();

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : String(value);
};

const normalizeTableMetrics = (input: unknown): QrTableMetrics[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .map((raw) => {
      const record = raw ?? {};
      if (typeof record !== 'object') {
        return null;
      }

      const candidate = record as Record<string, unknown>;

      const tableIdRaw = candidate.tableId ?? candidate.table_id;
      const tableId = typeof tableIdRaw === 'string' ? tableIdRaw : null;
      if (!tableId) {
        return null;
      }

      const tableNumber = toNullableNumber(candidate.tableNumber ?? candidate.table_number);
      const qrCodeSlug = toNullableString(candidate.qrCodeSlug ?? candidate.qr_code_slug);

      return {
        tableId,
        tableNumber,
        qrCodeSlug,
        scansCount: toNumber(candidate.scansCount ?? candidate.scans_count),
        ordersCount: toNumber(candidate.ordersCount ?? candidate.orders_count),
        revenueCents: toNumber(candidate.revenueCents ?? candidate.revenue_cents),
        lastActivityAt: toNullableString(candidate.lastActivityAt ?? candidate.last_activity_at),
      };
    })
    .filter((entry): entry is QrTableMetrics => Boolean(entry));
};

const normalizePeriod = (input: unknown): QrMetricsPeriodDescriptor => {
  const fallback: QrMetricsPeriodDescriptor = {
    period: 'today',
    timezone: 'UTC',
    label: 'Today',
    from: null,
    to: null,
    startUtc: null,
    endUtc: null,
  };

  if (!input || typeof input !== 'object') {
    return fallback;
  }

  const candidate = input as Record<string, unknown>;
  const periodRaw = candidate.period;
  const period =
    periodRaw === 'all' || periodRaw === 'custom' || periodRaw === 'today' ? periodRaw : fallback.period;

  return {
    period,
    timezone: typeof candidate.timezone === 'string' && candidate.timezone ? candidate.timezone : fallback.timezone,
    label: typeof candidate.label === 'string' && candidate.label ? candidate.label : fallback.label,
    from: toNullableString(candidate.from),
    to: toNullableString(candidate.to),
    startUtc: toNullableNumber(candidate.startUtc ?? candidate.start_utc),
    endUtc: toNullableNumber(candidate.endUtc ?? candidate.end_utc),
  };
};

const normalizeTotals = (input: unknown): QrMetricsTotals => {
  const candidate = (input ?? {}) as Record<string, unknown>;
  return {
    totalTables: toNumber(candidate.totalTables ?? candidate.total_tables),
    scansCount: toNumber(candidate.scansCount ?? candidate.scans_count),
    ordersCount: toNumber(candidate.ordersCount ?? candidate.orders_count),
    revenueCents: toNumber(candidate.revenueCents ?? candidate.revenue_cents),
    lastActivityAt: toNullableString(candidate.lastActivityAt ?? candidate.last_activity_at),
  };
};

export async function fetchQrMetrics(
  tenantSlug: string,
  options?: FetchQrMetricsOptions,
): Promise<QrMetricsResponse> {
  const params = new URLSearchParams();
  if (options?.period) {
    params.set('period', options.period);
  }
  if (options?.period === 'custom') {
    if (!options.from || !options.to) {
      throw new Error('Custom period requires both from and to values');
    }
  }
  if (options?.from) {
    params.set('from', options.from);
  }
  if (options?.to) {
    params.set('to', options.to);
  }

  const timezone = options?.timezone ?? getClientTimezone();
  if (timezone && !params.has('timezone')) {
    params.set('timezone', timezone);
  }

  const queryString = params.toString();
  const path = '/qr-codes/metrics' + (queryString ? `?${queryString}` : '');

  const payload = await requestJson<z.infer<typeof QrMetricsEnvelopeSchema>>(path, {
    signal: options?.signal,
    headers: { 'X-Tenant': tenantSlug },
    schema: QrMetricsEnvelopeSchema,
  });

  const metricsPayload = ((payload.metrics ?? payload) ?? {}) as Record<string, unknown>;
  if (!metricsPayload || typeof metricsPayload !== 'object') {
    throw new ApiResponseError('Invalid QR metrics payload', 200, metricsPayload);
  }

  return {
    generatedAt:
      typeof metricsPayload.generatedAt === 'string' && metricsPayload.generatedAt
        ? metricsPayload.generatedAt
        : new Date().toISOString(),
    period: normalizePeriod(metricsPayload.period),
    totals: normalizeTotals(metricsPayload.totals),
    tables: normalizeTableMetrics(metricsPayload.tables),
  };
}

const normalizeQrCodeStatus = (value: unknown): QrCodeStatus => {
  switch (value) {
    case 'revoked':
      return 'revoked';
    case 'expired':
      return 'expired';
    default:
      return 'active';
  }
};

const toHexString = (bytes: ArrayLike<number>): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

const parseFingerprint = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Uint8Array) {
    return toHexString(value);
  }
  if (Array.isArray(value)) {
    return toHexString(value as number[]);
  }
  return '';
};

const normalizeQrCodeSummary = (input: Record<string, unknown>): QrCodeSummary => ({
  id: String(input.id ?? ''),
  status: normalizeQrCodeStatus(input.status),
  tableId: String(input.tableId ?? ''),
  tableNumber: typeof input.tableNumber === 'number' ? input.tableNumber : input.tableNumber === null ? null : Number(input.tableNumber ?? null),
  displayOrder: Number(input.displayOrder ?? 0),
  qrCodeSlug: String(input.qrCodeSlug ?? ''),
  locationId: typeof input.locationId === 'string' ? input.locationId : null,
  zoneId: typeof input.zoneId === 'string' ? input.zoneId : null,
  zoneName: typeof input.zoneName === 'string' ? input.zoneName : null,
  zoneColor: typeof input.zoneColor === 'string' ? input.zoneColor : null,
  tokenFingerprint: parseFingerprint(
    input.tokenHash ?? input.token_hash ?? input.fingerprint ?? input.tokenFingerprint,
  ),
  issuedAt: Number(input.issuedAt ?? 0),
  expiresAt: Number(input.expiresAt ?? 0),
  revokedAt: input.revokedAt === null || input.revokedAt === undefined ? null : Number(input.revokedAt),
  revokedBy: typeof input.revokedBy === 'string' ? input.revokedBy : null,
  scanCount: Number(input.scanCount ?? 0),
  lastScannedAt:
    input.lastScannedAt === null || input.lastScannedAt === undefined ? null : Number(input.lastScannedAt),
  tamperCount: Number(input.tamperCount ?? 0),
  lastTamperAt:
    input.lastTamperAt === null || input.lastTamperAt === undefined ? null : Number(input.lastTamperAt),
  updatedAt: Number(input.updatedAt ?? 0),
});

const normalizeTamperIncident = (input: Record<string, unknown>): TamperIncidentSummary => {
  const metadataValue = input.metadata;
  const metadata =
    metadataValue && typeof metadataValue === 'object' && !Array.isArray(metadataValue)
      ? (metadataValue as Record<string, unknown>)
      : {};

  return {
    id: String(input.id ?? ''),
    qrCodeId: typeof input.qrCodeId === 'string' ? input.qrCodeId : null,
    tableId: typeof input.tableId === 'string' ? input.tableId : null,
    tableNumber: toNullableNumber(input.tableNumber ?? input.table_number),
    qrCodeSlug: toNullableString(input.qrCodeSlug ?? input.qr_code_slug),
    locationId: typeof input.locationId === 'string' ? input.locationId : null,
    locationName: toNullableString(input.locationName ?? input.location_name),
    zoneId: typeof input.zoneId === 'string' ? input.zoneId : null,
    zoneName: toNullableString(input.zoneName ?? input.zone_name),
    zoneColor: toNullableString(input.zoneColor ?? input.zone_color),
    severity: typeof input.severity === 'string' ? input.severity : 'info',
    source: toNullableString(input.source),
    description: toNullableString(input.description),
    metadata,
    detectedAt: Number(input.detectedAt ?? input.detected_at ?? 0),
    resolvedAt:
      input.resolvedAt === null || input.resolvedAt === undefined
        ? null
        : Number(input.resolvedAt ?? input.resolved_at ?? 0),
  };
};

export async function listQrCodes(
  tenantSlug: string,
  options?: ListQrCodesOptions,
): Promise<QrCodeSummary[]> {
  const params = new URLSearchParams();
  if (options?.statuses && options.statuses.length > 0) {
    params.set('status', options.statuses.join(','));
  }
  if (options?.tableIds && options.tableIds.length > 0) {
    params.set('tableId', options.tableIds.join(','));
  }
  if (options?.zoneId) {
    params.set('zoneId', options.zoneId);
  }
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }

  const payload = await requestJson<z.infer<typeof QrCodesResponseSchema>>(
    `/qr-codes${params.size ? `?${params.toString()}` : ''}`,
    {
      signal: options?.signal,
      headers: { 'X-Tenant': tenantSlug },
      schema: QrCodesResponseSchema,
    },
  );

  const codesRaw = Array.isArray(payload.codes) ? payload.codes : [];
  return codesRaw.map((raw) => normalizeQrCodeSummary(raw as Record<string, unknown>));
}

export const reorderQrTables = async (tenantSlug: string, tableIds: string[]): Promise<number> => {
  if (!Array.isArray(tableIds) || tableIds.length === 0) {
    throw new Error('tableIds is required');
  }

  const payload = await requestJson<z.infer<typeof QrReorderResponseSchema>>('/qr-codes/reorder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantSlug },
    body: { tableIds },
    schema: QrReorderResponseSchema,
  });

  return typeof payload.updated === 'number' ? payload.updated : tableIds.length;
};

export interface BulkAssignQrTablesPayload {
  tableIds: string[];
  locationId?: string | null;
  zoneId?: string | null;
}

export const bulkAssignQrTables = async (
  tenantSlug: string,
  payload: BulkAssignQrTablesPayload,
): Promise<number> => {
  if (!Array.isArray(payload.tableIds) || payload.tableIds.length === 0) {
    throw new Error('tableIds is required for bulk assignment');
  }

  const result = await requestJson<z.infer<typeof QrReorderResponseSchema>>('/qr-codes/bulk-assign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantSlug },
    body: payload,
    schema: QrReorderResponseSchema,
  });
  return typeof result.updated === 'number' ? result.updated : payload.tableIds.length;
};

const normalizeTimelineEntry = (input: Record<string, unknown>): QrTimelineEntry | null => {
  const id = typeof input.id === 'string' ? input.id : null;
  const tableId = typeof input.tableId === 'string' ? input.tableId : null;
  if (!id || !tableId) {
    return null;
  }

  const typeRaw = typeof input.type === 'string' ? input.type.toLowerCase() : '';
  const knownTypes: QrTimelineEntryType[] = ['issued', 'revoked', 'tamper', 'incident', 'regenerated', 'refresh'];
  const type = knownTypes.includes(typeRaw as QrTimelineEntryType) ? (typeRaw as QrTimelineEntryType) : 'issued';

  const occurredAt =
    typeof input.occurredAt === 'number'
      ? input.occurredAt
      : Number.isFinite(Number(input.occurredAt))
        ? Number(input.occurredAt)
        : 0;

  const severityRaw = typeof input.severity === 'string' ? input.severity.toLowerCase() : '';
  const severity: QrTimelineSeverity =
    severityRaw === 'critical' || severityRaw === 'warning' || severityRaw === 'info'
      ? (severityRaw as QrTimelineSeverity)
      : 'info';

  const resolvedAt =
    input.resolvedAt === null || input.resolvedAt === undefined
      ? null
      : typeof input.resolvedAt === 'number'
        ? input.resolvedAt
        : Number(input.resolvedAt);

  const metadata =
    input.metadata && typeof input.metadata === 'object' && !Array.isArray(input.metadata)
      ? (input.metadata as Record<string, unknown>)
      : null;

  return {
    id,
    tableId,
    qrCodeId: typeof input.qrCodeId === 'string' ? input.qrCodeId : null,
    type,
    occurredAt,
    severity,
    description: typeof input.description === 'string' ? input.description : null,
    source: typeof input.source === 'string' ? input.source : null,
    actorId: typeof input.actorId === 'string' ? input.actorId : null,
    resolvedAt: resolvedAt !== null && Number.isFinite(resolvedAt) ? resolvedAt : null,
    metadata,
  };
};

export const fetchQrTimeline = async (
  tenantSlug: string,
  tableId: string,
  options?: FetchQrTimelineOptions,
): Promise<QrTimelineEntry[]> => {
  if (!tenantSlug) {
    throw new Error('tenant slug is required for QR timeline');
  }
  if (!tableId) {
    throw new Error('tableId is required for QR timeline');
  }

  const params = new URLSearchParams();
  if (options?.limit && Number.isFinite(options.limit)) {
    params.set('limit', String(options.limit));
  }
  if (options?.since && Number.isFinite(options.since)) {
    params.set('since', String(options.since));
  }
  const path = `/qr/${encodeURIComponent(tableId)}/logs${params.size ? `?${params.toString()}` : ''}`;
  const payload = await requestJson<z.infer<typeof QrTimelineResponseSchema>>(path, {
    signal: options?.signal,
    headers: { 'X-Tenant': tenantSlug },
    schema: QrTimelineResponseSchema,
  });

  const entriesRaw = Array.isArray(payload.entries) ? payload.entries : [];

  return entriesRaw
    .map((entry) => normalizeTimelineEntry(entry))
    .filter((entry): entry is QrTimelineEntry => Boolean(entry));
};

export const listTamperIncidents = async (
  tenantSlug: string,
  options?: ListTamperIncidentsOptions,
): Promise<TamperIncidentSummary[]> => {
  const params = new URLSearchParams();
  if (options?.tableId) {
    params.set('tableId', options.tableId);
  }
  if (options?.severity) {
    params.set('severity', options.severity);
  }
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }
  if (options?.since) {
    params.set('since', options.since);
  }

  const payload = await requestJson<z.infer<typeof QrTamperIncidentsResponseSchema>>(
    `/qr-codes/tamper-incidents${params.size ? `?${params.toString()}` : ''}`,
    {
      headers: { 'X-Tenant': tenantSlug },
      signal: options?.signal,
      schema: QrTamperIncidentsResponseSchema,
    },
  );

  const incidentsRaw = Array.isArray(payload.incidents) ? payload.incidents : [];
  return incidentsRaw.map((raw) => normalizeTamperIncident(raw as Record<string, unknown>));
};

export async function revokeQrCode(
  tenantSlug: string,
  qrCodeId: string,
  reason?: string,
): Promise<void> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/qr-codes/revoke', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantSlug },
    body: JSON.stringify({ qrCodeId, reason }),
  });
  await assertOk(response);
}

export async function exportQrAnalyticsCsv(
  tenantSlug: string,
  options?: FetchQrMetricsOptions,
): Promise<Blob> {
  const params = new URLSearchParams();
  if (options?.period) {
    params.set('period', options.period);
  }
  if (options?.from) {
    params.set('from', options.from);
  }
  if (options?.to) {
    params.set('to', options.to);
  }

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/qr-codes/analytics/export${params.size ? `?${params.toString()}` : ''}`, {
    headers: { Accept: 'text/csv', 'X-Tenant': tenantSlug },
  });

  await assertOk(response);
  return response.blob();
}
